import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNative } from '../utils/platform';
import { useMobile } from '../hooks/useMobile';

/**
 * PwaPrompts — web-only PWA affordances:
 *   1. "Install app" banner when the browser fires `beforeinstallprompt`, shown
 *      only on mobile-width viewports — installing to a home screen is a phone
 *      affordance, and the banner is styled to sit above the mobile nav. Desktop
 *      Chrome still offers its own address-bar install button. iOS Safari never
 *      fires the event — users add via the Share sheet — so nothing renders
 *      there, which is correct.
 *   2. "Update available" banner when a new service worker takes control, so an
 *      open session can refresh to the new build.
 *
 * Renders nothing inside Capacitor (native ships its own binary updates) and
 * nothing until there is actually something to show — safe to mount globally.
 */
// A dismissal is remembered for this long. Long enough that the banner stops
// being furniture on a phone — it sits above the bottom nav and covers a row of
// whatever list is open — but not forever, since someone who taps it away in
// passing may still want the app on their home screen later.
const SNOOZE_DAYS = 30;
const SNOOZE_KEY = 'pwa-install-dismissed-at';

function snoozedUntilRecently() {
  try {
    const at = Number(window.localStorage.getItem(SNOOZE_KEY));
    if (!at) return false;
    return Date.now() - at < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false; // private mode, blocked storage: show the banner
  }
}

export default function PwaPrompts() {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const [installEvent, setInstallEvent] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(snoozedUntilRecently);

  // 1. Capture the install prompt.
  useEffect(() => {
    if (isNative) return undefined;
    const onPrompt = (e) => {
      e.preventDefault(); // stash it; we trigger it from our own button
      setInstallEvent(e);
    };
    const onInstalled = () => setInstallEvent(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // 2. Detect a newly-activated service worker.
  useEffect(() => {
    if (isNative) return undefined;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    let reloading = false;
    // The SW calls skipWaiting()+clients.claim(), so a new build activates and
    // fires controllerchange. Ignore the very first controller (initial install).
    const hadController = !!navigator.serviceWorker.controller;
    const onControllerChange = () => {
      if (!hadController || reloading) return;
      setUpdateReady(true);
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  const handleInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    try { await installEvent.userChoice; } catch { /* dismissed */ }
    setInstallEvent(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch { /* not fatal */ }
  };

  const showInstall = !!installEvent && isMobile && !dismissed;

  if (isNative || (!showInstall && !updateReady)) return null;

  const banner = {
    position: 'fixed',
    insetInline: '12px',
    // inset-inline alone sizes this against the initial containing block, which
    // on an RTL page that scrolls horizontally is the scrollable width, not the
    // viewport — so the banner grew with the overflow and fed it. The cap keeps
    // it to the visible width whatever the document is doing.
    maxWidth: 'calc(100vw - 24px)',
    boxSizing: 'border-box',
    bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
    // Above the bottom nav (1000), below the side drawer's backdrop (1030) and
    // every modal above it. At 2000 the banner floated over an open drawer,
    // covering the log-out row — an ambient prompt should be the thing that
    // gets dimmed when something is opened over it, not the other way round.
    zIndex: 1010,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'var(--color-surface, #fff)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    border: '1px solid var(--color-border, #E2E6EE)',
    fontSize: '14px',
    color: 'var(--color-text-primary, #1A1F36)',
  };
  const action = {
    marginInlineStart: 'auto',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--color-primary-dark, #355760)',
    color: 'var(--color-on-primary, #fff)',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  if (updateReady) {
    return (
      <div style={banner} role="status">
        <span>{t('pwa.updateAvailable', 'A new version is available.')}</span>
        <button style={action} onClick={() => window.location.reload()}>
          {t('pwa.refresh', 'Refresh')}
        </button>
      </div>
    );
  }

  const dismiss = {
    marginInlineStart: '4px',
    padding: '8px',
    lineHeight: 1,
    border: 'none',
    background: 'none',
    color: 'var(--color-text-tertiary, #697386)',
    fontSize: '18px',
    cursor: 'pointer',
  };

  return (
    <div style={banner} role="dialog" aria-label={t('pwa.installTitle', 'Install app')}>
      <span>{t('pwa.installPrompt', 'Install CenterHub on your device for quick access.')}</span>
      <button style={action} onClick={handleInstall}>
        {t('pwa.install', 'Install')}
      </button>
      <button
        style={dismiss}
        className="tap-target"
        onClick={handleDismiss}
        aria-label={t('common.dismiss', 'Dismiss')}
        title={t('common.dismiss', 'Dismiss')}
      >
        ×
      </button>
    </div>
  );
}
